import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOrganizationWasteTypes,
  type OrganizationWasteType,
} from '../services/organization.service';

export const ORGANIZATION_WASTE_TYPES_QUERY_KEY = 'organization-waste-types';

export interface WasteTypeCategoryGroup {
  category: string;
  rows: OrganizationWasteType[];
}

function unwrap(response: unknown): OrganizationWasteType[] {
  const rows =
    response && typeof response === 'object' && 'result' in response
      ? (response as { result: unknown }).result
      : response;
  return Array.isArray(rows) ? (rows as OrganizationWasteType[]) : [];
}

/** The waste types an organization handles (its basket), the one list every
 *  market picker draws from. Rows carry both the catalog material id and the
 *  material_types alias id, so callers can key whichever the backend expects. */
export function useOrganizationWasteTypes(organizationId: string | undefined, enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [ORGANIZATION_WASTE_TYPES_QUERY_KEY, organizationId],
    queryFn: async () => unwrap(await getOrganizationWasteTypes(organizationId!)),
    enabled: Boolean(organizationId) && enabled,
    staleTime: 30_000,
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);

  const byMaterialTypeId = useMemo(() => {
    const map = new Map<string, OrganizationWasteType>();
    rows.forEach((row) => {
      if (row.material_type_id) map.set(row.material_type_id, row);
    });
    return map;
  }, [rows]);

  const byMaterialId = useMemo(() => {
    const map = new Map<string, OrganizationWasteType>();
    rows.forEach((row) => map.set(row.material_id, row));
    return map;
  }, [rows]);

  const groupedByCategory = useMemo<WasteTypeCategoryGroup[]>(() => {
    const groups = new Map<string, OrganizationWasteType[]>();
    rows.forEach((row) => {
      // The short catalog code ("plastic") is what locale labels, dashboard
      // chips and appetite lines use; the material's parent_category is a
      // display name ("Cardboard and Paper").
      const key = row.category_code ?? row.parent_category ?? '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, groupRows]) => ({
        category,
        rows: [...groupRows].sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [rows]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [ORGANIZATION_WASTE_TYPES_QUERY_KEY, organizationId] });

  return {
    rows,
    byMaterialTypeId,
    byMaterialId,
    groupedByCategory,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    invalidate,
  };
}
